import { useEntriesForProject } from "@app/hooks/useEntries";
import { useEntryMutations } from "@app/hooks/useEntryMutations";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import type { EntryForDisplay } from "../interfaces/CapsuleEntry";
import type { CapsuleParty } from "../interfaces/CapsuleParty";
import type { ProjectSummary } from "../interfaces/ProjectSummary";
import { Dialog } from "./Dialog";
import { EditEntryCard } from "./EditEntryCard";
import { ProjectSummaryHeader } from "./ProjectSummaryHeader";
import { Trash } from "./SVGs";
import { WithQuery, WithSpinner } from "./WithQuery";

interface EntrySummaryCardProps {
	project: ProjectSummary;
	peopleData: CapsuleParty[];
	supportHours?: string;
	commencementDate?: Date;
	contractTerm?: string;
}

export const EntrySummaryCard = ({
	project,
	peopleData,
	...props
}: EntrySummaryCardProps) => {
	const [deleting, setDeleting] = useState<EntryForDisplay | null>(null);
	const [editing, setEditing] = useState<EntryForDisplay | null>(null);

	const entries = useEntriesForProject({ project });

	const { deleteEntryQuery } = useEntryMutations();

	const [supportHours, commencementDate, contractTerm] = useMemo(() => {
		if (
			props.supportHours != null &&
			props.commencementDate != null &&
			props.contractTerm != null
		) {
			const supportHours = Number(props.supportHours);
			const commencementDate = props.commencementDate;
			const contractTerm = Number(props.contractTerm?.replace(" Year", ""));
			return [supportHours, commencementDate, contractTerm];
		}
		return [undefined, undefined, undefined];
	}, [props.supportHours, props.commencementDate, props.contractTerm]);

	const calculateMinutesWorked = useMemo(() => {
		if (entries.data == null) {
			return "";
		}
		let totalMinutes: number = 0;
		(entries.data as EntryForDisplay[]).forEach((f: EntryForDisplay) => {
			totalMinutes += f.minutes;
		});
		return totalMinutes;
	}, [entries.data]);

	const syncIssue = useMemo(() => {
		return (
			calculateMinutesWorked !== "" &&
			project.minutes_worked.toFixed(1) !== "" &&
			Number(calculateMinutesWorked) !== project.minutes_worked
		);
	}, [calculateMinutesWorked, project.minutes_worked]);

	useEffect(() => {
		if (syncIssue) {
			Sentry.withScope((scope) => {
				scope.setFingerprint(["calculatedDaysWorked", project.project_id]);
				Sentry.captureMessage(
					project.project_id +
						": Summary is out of sync with the Entries Data - Summary is: " +
						project.minutes_worked.toFixed(2) +
						" and sum of entries is: " +
						calculateMinutesWorked,
				);
			});
		}
	}, [
		calculateMinutesWorked,
		project.minutes_worked,
		project.project_id,
		syncIssue,
	]);

	return (
		<WithQuery
			showLoading={true}
			data={"the Project History"}
			query={entries}
			render={(entriesData: EntryForDisplay[] | null) => {
				if (!entriesData) return null;
				let csvData =
					'"Date","Time","Consultant","Activity","Details of Work","Requisitioner","Minutes Worked"\n';
				(entriesData || []).forEach((e) => {
					csvData +=
						'"' +
						e.date +
						'","' +
						e.time +
						'","' +
						e.consultant +
						'","' +
						e.activity +
						'","' +
						e.details +
						'","' +
						(e.requisitioner || "") +
						'","' +
						e.minutes +
						'"\n';
				});

				return (
					<div className={"padded scrollable"}>
						<div key={"history_card"} className={"flex flex-col card padded"}>
							<div className="flex-row center">
								<ProjectSummaryHeader
									project={project}
									text={"Project Summary"}
									supportHours={supportHours}
									commencementDate={commencementDate}
									contractTerm={contractTerm}
									entriesData={entriesData}
								/>
								{entriesData.length !== 0 && (
									<a
										className="no-link-a-tag"
										download="report.csv"
										href={
											"data:text/csv;charset=UTF-8," +
											encodeURIComponent(csvData)
										}
									>
										<button
											type={"button"}
											className="button button-primary margin-right"
										>
											Download History
										</button>
									</a>
								)}
							</div>
							{syncIssue ? (
								<span className={"warning"}>
									Days Used Summary may be out of Sync - consider refreshing.
								</span>
							) : null}
							<div>
								{deleting != null && (
									<WithSpinner loading={deleteEntryQuery.isPending}>
										<Dialog>
											<div className="flex center">
												<h3>Are You Sure?</h3>
												<p>
													This will remove the timesheet entry and update the
													Project Summary to reflect the new Days Used and Days
													Remaining.
												</p>
												<p>Click 'Yes' to continue, or 'Cancel' to cancel.</p>
												<div className="flex flex-row">
													<button
														className={"button button-danger"}
														type={"button"}
														disabled={false}
														onClick={() => {
															deleteEntryQuery.mutate({
																timeTrack: deleting,
																project: project,
																onSuccess: () => {
																	setDeleting(null);
																},
															});
														}}
													>
														Yes
													</button>
													<button
														className={"button button-primary"}
														type={"button"}
														onClick={() => setDeleting(null)}
													>
														Cancel
													</button>
												</div>
											</div>
										</Dialog>
									</WithSpinner>
								)}
								{editing != null && (
									<Dialog>
										<EditEntryCard
											original={editing}
											project={project}
											setEditing={setEditing}
											peopleData={peopleData}
										/>
									</Dialog>
								)}
								<div className="flex">
									<table className={"table"}>
										<thead>
											<tr className="table-header">
												<th>Date</th>
												<th>Time</th>
												<th>Consultant</th>
												<th>Activity</th>
												<th>Link</th>
												<th>Details of Work</th>
												<th>Requisitioner</th>
												<th>Minutes Worked</th>
											</tr>
										</thead>
										<tbody>
											{entriesData.length === 0 && (
												<tr>
													<td className={"text-center"} colSpan={8}>
														<i>No Activity Logged</i>
													</td>
												</tr>
											)}
											{entriesData.map((e: EntryForDisplay) => {
												const jiraLink = ExtractJiraLink(e.details);
												return (
													<tr
														key={`${e.date}_${e.time}_${Math.random() * 10}`}
														className={"table-row"}
													>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.date}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.time}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.consultant}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.activity}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	!jiraLink && setEditing(e);
																}}
															>
																{jiraLink}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.details}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.requisitioner || "-"}
															</button>
														</td>
														<td className="table-column">
															<button
																type={"button"}
																className={"button-div"}
																onClick={() => {
																	setEditing(e);
																}}
															>
																{e.minutes}
															</button>
														</td>
														<td className="table-column">
															<div className={"tooltip"}>
																<button
																	title={"Delete Entry"}
																	onClick={() => {
																		setDeleting(e);
																	}}
																	className="select-clear"
																	type={"button"}
																>
																	<Trash />
																</button>
																<span className="tooltip-text">
																	Delete Entry
																</span>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				);
			}}
		/>
	);
};

function ExtractJiraLink(e: string): React.ReactNode | null {
	let jira: React.ReactNode | null = null;
	const regex = /CS-\d*/;
	const link = regex.exec(e);
	if (link != null) {
		jira = (
			<a
				rel={"noopener noreferrer"}
				target={"_blank"}
				href={
					"https://chartistechnology.atlassian.net/jira/servicedesk/projects/CS/queues/custom/1/" +
					link[0]
				}
			>
				{"Jira"}
			</a>
		);
	}
	return jira;
}
